import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import type { AppConfig } from '@/config/AppConfig';
import { getRuntimeConfig } from '@/config/runtimeConfig';

/**
 * Initialize Sentry with project-specific configuration
 */
export function initSentry(config?: AppConfig) {
  // Don't initialize Sentry during local development
  if (import.meta.env.DEV) {
    return;
  }

  // Don't initialize Sentry when running on localhost (any port)
  if (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  ) {
    return;
  }

  // Get DSN from passed config or runtime config (always present in production)
  const sentryDsn = config?.sentryDsn ?? getRuntimeConfig().sentryDsn;

  Sentry.init({
    // Unique URL that connects to your Sentry project
    // Use DSN from auth0 config (fetched from AWS SSM during build)
    dsn: sentryDsn,
    // Environment context (development, production, etc.)
    environment: import.meta.env.MODE,
    integrations: [
      // Tracks React Router navigation and performance
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [window.location.origin],
      }),
      // Tracks outgoing HTTP requests with W3C Trace Context
      Sentry.browserTracingIntegration({
        // Automatically instrument fetch and XHR
        traceFetch: true,
        traceXHR: true,
      }),
    ],
    // Include user info (IP, etc) in error reports
    sendDefaultPii: true,
    // Percentage of performance traces to capture (1.0 = 100%)
    // Sentry charges based on the number of traces,
    // So we'll only capture 10% of them
    tracesSampleRate: 0.1,

    // Percentage of session replays to capture (1.0 = 100%)
    // 50% in production/staging, 0% in development
    replaysSessionSampleRate: import.meta.env.MODE === 'development' ? 0 : 0.5,
    // Percentage of replays to capture on error (1.0 = 100%)
    replaysOnErrorSampleRate: 1.0,

    // URLs to which Sentry will attach tracing headers (traceparent, baggage)
    // This ensures distributed tracing works across frontend and backend
    // Match all API calls to our backend
    tracePropagationTargets: [
      /^\/api\//,
      // Add your backend domain if different from frontend
      // e.g., 'api.example.com', /^https:\/\/api\.example\.com/
    ],
  });
}

/**
 * Set user email in Sentry for error tracking and session replay
 *
 * @param {string | null | undefined} email - User email to set, or null/undefined to clear user context
 */
export function setSentryUser(email: string | null | undefined): void {
  if (email) {
    Sentry.setUser({ email });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Sentry error handler for uncaught errors
 * Catches errors that Error Boundaries miss:
 * - Event handlers (onClick, onChange, etc.)
 * - Async code (promises, setTimeout)
 * - Side effects (useEffect callbacks)
 * - Any other uncaught JavaScript errors
 */
export const sentryErrorHandler = Sentry.reactErrorHandler(
  (error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  },
);
